import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { ApiService } from './base-api/api.service';
import {
  AwardStatus,
  AwardStatusChange,
  CreateScholarshipAward,
  GrantCycleAwards,
  GrantCycleAwardsResult,
  ScholarshipAwardDetail,
  ScholarshipAwardSummary,
  StudentDashboard,
  UpdateScholarshipAward,
} from '../models/scholarship-award';

/**
 * Scholarship awards: the student dashboard (award, budget and duration
 * tracker) and the administrator award console.
 */
@Injectable({ providedIn: 'root' })
export class AwardApiService {
  private readonly api = inject(ApiService);

  // ---- Student -----------------------------------------------------------

  /** Everything the student portal needs on load. */
  getMyDashboard(): Observable<StudentDashboard> {
    return this.api
      .get<{ data: StudentDashboard }>('awards/my-dashboard')
      .pipe(map((response) => response.data));
  }

  getMyActiveAward(): Observable<ScholarshipAwardDetail> {
    return this.api
      .get<{ data: ScholarshipAwardDetail }>('awards/my-active')
      .pipe(map((response) => response.data));
  }

  /** A completed award stays readable for the alumni view. */
  getMyAward(id: number): Observable<ScholarshipAwardDetail> {
    return this.api
      .get<{ data: ScholarshipAwardDetail }>(`awards/my-awards/${id}`)
      .pipe(map((response) => response.data));
  }

  // ---- Administrator -----------------------------------------------------

  getAwardsForReview(
    cycle?: string | null,
    scholarshipId?: number | null,
    status?: AwardStatus | null,
    search?: string
  ): Observable<ScholarshipAwardSummary[]> {
    const params = new URLSearchParams();
    if (cycle) {
      params.set('cycle', cycle);
    }
    if (scholarshipId) {
      params.set('scholarshipId', String(scholarshipId));
    }
    if (status) {
      params.set('status', status);
    }
    if (search) {
      params.set('search', search);
    }
    const query = params.toString();

    return this.api
      .get<{ data: ScholarshipAwardSummary[] }>(`admin/awards${query ? `?${query}` : ''}`)
      .pipe(map((response) => response.data));
  }

  getAward(id: number): Observable<ScholarshipAwardDetail> {
    return this.api
      .get<{ data: ScholarshipAwardDetail }>(`admin/awards/${id}`)
      .pipe(map((response) => response.data));
  }

  createAward(request: CreateScholarshipAward): Observable<ScholarshipAwardDetail> {
    return this.api
      .post<{ data: ScholarshipAwardDetail }>('admin/awards', request)
      .pipe(map((response) => response.data));
  }

  updateAward(id: number, request: UpdateScholarshipAward): Observable<ScholarshipAwardDetail> {
    return this.api
      .put<{ data: ScholarshipAwardDetail }>(`admin/awards/${id}`, request)
      .pipe(map((response) => response.data));
  }

  changeStatus(id: number, request: AwardStatusChange): Observable<ScholarshipAwardDetail> {
    return this.api
      .put<{ data: ScholarshipAwardDetail }>(`admin/awards/${id}/status`, request)
      .pipe(map((response) => response.data));
  }

  /** Awards every approved application for a cycle. Idempotent. */
  grantCycleAwards(request: GrantCycleAwards): Observable<GrantCycleAwardsResult> {
    return this.api
      .post<{ data: GrantCycleAwardsResult }>('admin/awards/grant-cycle', request)
      .pipe(map((response) => response.data));
  }

  awardFromApplication(applicationId: number): Observable<ScholarshipAwardDetail> {
    return this.api
      .post<{ data: ScholarshipAwardDetail }>(`admin/awards/from-application/${applicationId}`, {})
      .pipe(map((response) => response.data));
  }
}
